import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, KeyRound, Mail, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { fmtDT, gstatus } from "@/components/graphisme/gUtils";
import { DriveExplorer } from "@/components/graphisme/Drive";

export function ClientGraphismeTab({ clientId }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  useEffect(() => {
    api.get(`/graphisme/projects?client_id=${clientId}`).then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, [clientId]);
  if (projects === null) return <div className="p-6 text-center"><div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin mx-auto" /></div>;
  return (
    <div className="space-y-3" data-testid="client-graphisme-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-neutral-500">{projects.length} projet(s) graphique(s) pour ce client</p>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => navigate("/graphisme")} data-testid="client-graphisme-goto">
          <Palette className="w-3.5 h-3.5" /> Ouvrir Graphisme
        </Button>
      </div>
      {projects.length === 0 && <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">Aucun projet graphique pour ce client.</p>}
      {projects.map((p) => {
        const st = gstatus(p.status);
        return (
          <button key={p.id} onClick={() => navigate(`/graphisme/projet/${p.id}`)} data-testid={`client-gproject-${p.id}`}
            className="w-full flex items-center justify-between gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3 hover:border-neutral-400 transition-colors text-left">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{p.name}</p>
              <p className="text-[11px] text-neutral-400 font-mono">{p.number}</p>
            </div>
            <span className="flex items-center gap-2 shrink-0">
              {p.price_ht > 0 && <span className="text-sm font-medium">{eur(p.price_ht)}</span>}
              <span className={`text-[11px] rounded-full px-2.5 py-1 border ${st.cls}`}>{st.label}</span>
            </span>
          </button>
        );
      })}
      <div className="pt-3 border-t border-neutral-100 space-y-3" data-testid="client-drive-section">
        <h3 className="font-heading font-bold text-sm">Documents partagés avec le client</h3>
        <DriveExplorer side="agency" clientId={clientId} />
      </div>
    </div>
  );
}

const USER_PERMISSION_LABELS = [
  ["view_projects", "Voir les projets"], ["new_request", "Envoyer une demande"],
  ["add_files", "Ajouter des fichiers"], ["validate_creations", "Valider les créations"],
  ["view_quotes", "Voir les devis"], ["validate_quotes", "Valider les devis"],
  ["view_invoices", "Voir les factures"],
];

export function GraphismeUsersCard({ clientId }) {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", function: "",
    permissions: { validate_creations: true } });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get(`/graphisme/clients/${clientId}/users`).then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, [clientId]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/graphisme/clients/${clientId}/users`, form);
      toast.success(r.data.password
        ? `Utilisateur créé — mot de passe généré : ${r.data.password} (notez-le)`
        : "Utilisateur créé", { duration: 12000 });
      setForm({ first_name: "", last_name: "", email: "", function: "", permissions: { validate_creations: true } });
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const invite = async (u) => {
    try {
      await api.post(`/graphisme/clients/${clientId}/users/${u.id}/send-link`, { kind: "invite" });
      toast.success(`Invitation envoyée à ${u.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
  };

  if (users === null) return null;
  return (
    <div className="pt-4 border-t border-neutral-100" data-testid="graphisme-users-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-bold flex items-center gap-2"><KeyRound className="w-4 h-4" /> Utilisateurs / Décideurs</h3>
        <Button size="sm" variant="outline" className="gap-1 text-xs" data-testid="gu-add"
          onClick={() => setShowForm((v) => !v)}>+ Ajouter un utilisateur</Button>
      </div>
      <p className="text-xs text-neutral-400 mb-3">Chaque utilisateur dispose de son propre accès ; ses validations sont nominatives.</p>
      {users.length === 0 && !showForm && (
        <p className="text-xs text-neutral-400" data-testid="gu-empty">Aucun utilisateur additionnel — le compte principal suffit.</p>
      )}
      <div className="space-y-2" data-testid="gu-users">
        {users.map((u) => (
          <div key={u.id} className="border border-neutral-200 rounded-lg px-3 py-2.5 text-sm" data-testid={`gu-user-${u.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <p className="font-medium">{u.first_name} {u.last_name}
                  {u.active ? <span className="ml-2 text-[10px] text-green-600">🟢 Actif</span>
                            : <span className="ml-2 text-[10px] text-neutral-400">Inactif</span>}
                </p>
                <p className="text-xs text-neutral-400">{u.email}{u.function ? ` · ${u.function}` : ""}</p>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`gu-invite-${u.id}`} onClick={() => invite(u)}>Inviter</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`gu-toggle-${u.id}`}
                  onClick={async () => { await api.patch(`/graphisme/clients/${clientId}/users/${u.id}`, { active: !u.active }); load(); }}>
                  {u.active ? "Désactiver" : "Activer"}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" data-testid={`gu-delete-${u.id}`}
                  onClick={async () => { await api.delete(`/graphisme/clients/${clientId}/users/${u.id}`); toast.success("Utilisateur supprimé"); load(); }}>
                  Supprimer
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {USER_PERMISSION_LABELS.filter(([k]) => u.permissions?.[k]).map(([k, label]) => (
                <span key={k} className="text-[10px] bg-neutral-100 text-neutral-600 rounded-full px-2 py-0.5">{label}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      {showForm && (
        <div className="border border-neutral-200 rounded-lg p-3 mt-2 space-y-2 bg-neutral-50" data-testid="gu-form">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Prénom *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="gu-first" />
            <Input placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="gu-last" />
            <Input placeholder="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="gu-email" />
            <Input placeholder="Fonction" value={form.function} onChange={(e) => setForm({ ...form, function: e.target.value })} data-testid="gu-function" />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {USER_PERMISSION_LABELS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`gu-perm-${k}`}>
                <input type="checkbox" checked={!!form.permissions[k]}
                  onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, [k]: e.target.checked } })} className="rounded" />
                {label}
              </label>
            ))}
          </div>
          <Button size="sm" onClick={submit} disabled={busy || !form.first_name.trim() || !form.email.trim()}
            className="bg-neutral-900 hover:bg-neutral-800 text-white text-xs" data-testid="gu-save">
            {busy ? "Création…" : "Créer l'utilisateur (mot de passe généré)"}
          </Button>
        </div>
      )}
    </div>
  );
}

export function GraphismeAccessCard({ clientId }) {
  const [access, setAccess] = useState(null);
  const [password, setPassword] = useState("");
  const [generated, setGenerated] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/graphisme/client-access/${clientId}`).then((r) => setAccess(r.data)).catch(() => {});
  useEffect(() => { load(); }, [clientId]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/graphisme/client-access/${clientId}`, { password: password || undefined });
      setGenerated(r.data.password || "");
      setPassword("");
      toast.success("Accès client enregistré");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const togglePlatform = async (pid) => {
    const cur = access.platforms || [];
    const next = cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid];
    if (!next.length) { toast.error("Sélectionnez au moins une plateforme"); return; }
    try {
      const r = await api.patch(`/graphisme/client-access/${clientId}/platforms`, { platforms: next });
      setAccess((a) => ({ ...a, platforms: r.data.platforms }));
      toast.success("Plateformes mises à jour — effet immédiat");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!access) return null;
  return (
    <div className="pt-4 border-t border-neutral-100" data-testid="graphisme-access-card">
      <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><KeyRound className="w-4 h-4" /> Accès à l'espace client</h3>
      <p className="text-sm text-neutral-500 mb-3">Identifiant + mot de passe du portail client DGA — un seul compte pour toutes les prestations. Distinct du lien de validation CM.</p>
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-sm space-y-1 mb-3">
        <p>Statut : <strong data-testid="access-status">{access.enabled ? "Actif" : "Désactivé"}</strong></p>
        <p>Identifiant : <strong data-testid="access-email">{access.email || "—"}</strong></p>
        <p className="text-xs text-neutral-400">Dernière connexion : {access.last_login ? fmtDT(access.last_login) : "jamais"}</p>
      </div>
      {access.enabled && (
        <div className="mb-3" data-testid="access-platforms">
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-1.5">Plateformes accessibles au client</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {[["cm", "Community Management"], ["graphisme", "Graphisme"], ["web", "Web Design"]].map(([pid, label]) => (
              <label key={pid} className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid={`access-plat-${pid}`}>
                <input type="checkbox" className="rounded" checked={(access.platforms || []).includes(pid)}
                  onChange={() => togglePlatform(pid)} />
                {label}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-neutral-400 mt-1.5">Décocher retire immédiatement l'accès côté client — les données et projets sont conservés.</p>
        </div>
      )}
      {generated && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 text-sm flex items-center justify-between gap-2" data-testid="access-generated">
          <span>Mot de passe généré : <code className="font-mono font-bold">{generated}</code> (notez-le, il ne sera plus affiché)</span>
          <button onClick={() => { navigator.clipboard.writeText(generated); toast.success("Copié"); }} className="text-green-700 hover:text-green-900"><Copy className="w-4 h-4" /></button>
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        <Input type="text" placeholder="Nouveau mot de passe (vide = généré)" value={password}
          onChange={(e) => setPassword(e.target.value)} className="max-w-xs" data-testid="access-password" />
        <Button size="sm" onClick={save} disabled={busy} className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="access-save">
          {access.enabled ? "Réinitialiser le mot de passe" : "Créer l'accès"}
        </Button>
        {access.enabled && (
          <Button size="sm" variant="outline" className="text-red-600 border-red-200" data-testid="access-disable"
            onClick={async () => { await api.delete(`/graphisme/client-access/${clientId}`); toast.success("Accès désactivé"); load(); }}>
            Désactiver
          </Button>
        )}
      </div>
      {access.enabled && (
        <div className="flex gap-2 flex-wrap mt-2" data-testid="access-actions">
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="access-copy-link"
            onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/connexion`); toast.success("Lien de connexion client copié"); }}>
            <Copy className="w-3.5 h-3.5" /> Copier le lien de connexion
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" disabled={busy} data-testid="access-send-invite"
            onClick={async () => {
              setBusy(true);
              try {
                const r = await api.post(`/graphisme/client-access/${clientId}/send-link`, { kind: "invite" });
                toast.success(`Invitation envoyée à ${r.data.email}`);
              } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
              finally { setBusy(false); }
            }}>
            <Mail className="w-3.5 h-3.5" /> Envoyer les accès
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" disabled={busy} data-testid="access-send-reset"
            onClick={async () => {
              setBusy(true);
              try {
                const r = await api.post(`/graphisme/client-access/${clientId}/send-link`, { kind: "reset" });
                toast.success(`Lien de réinitialisation envoyé à ${r.data.email}`);
              } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
              finally { setBusy(false); }
            }}>
            <Mail className="w-3.5 h-3.5" /> Réinitialisation par email
          </Button>
        </div>
      )}
    </div>
  );
}
